<template>
  <div class="calendar" :class="clsx(`calendar--${size}`, { 'calendar-disabled': disable })">
    <CalendarHeader
      :disable-calendar="disable"
      :size="size"
      :monthName="monthName"
      :selectedYear="selectedYear"
      @navigate-month="navigateMonth"
      @toggle-month-selector="toggleSelector('months')"
      @toggle-year-selector="toggleSelector('years')"
    />

    <div class="calendar-wrapper">
      <transition name="fade" mode="out-in">
        <CalendarGrid
          v-if="!showingMonths && !showingYears"
          :class="clsx('calendar-grid', { 'calendar-grid__alignment': gridFixedHeight })"
          :disable-calendar="disable"
          :size="size"
          :dayHeaders="dayHeaders"
          :calendarDates="calendarDates"
          :range="range"
          :maxRangeDays="maxRangeDays"
          :startDate="startDate"
          :selectingRange="selectingRange"
          @selectDate="handleDateSelection"
          key="calendar-grid"
        />
        <MonthsGrid
          v-else-if="showingMonths"
          :class="clsx('months-grid', { 'months-grid__alignment': gridFixedHeight })"
          :show="showingMonths"
          :size="size"
          :monthNames="monthNames"
          :currentDate="currentDate"
          :selectedYear="selectedYear"
          :range="range"
          :startDate="startDate"
          :endDate="endDate"
          :maxRangeLimit="maxRangeDays"
          :limitUnit="'day'"
          :selectingRange="selectingRange"
          :available-dates="availableDates"
          @select-month="handleMonthSelection"
          key="months-grid"
        />
        <YearsGrid
          v-else-if="showingYears"
          :class="clsx('years-grid', { 'years-grid__alignment': gridFixedHeight })"
          :show="showingYears"
          :size="size"
          :yearOptions="yearOptions"
          :selectedYear="selectedYear"
          :range="range"
          :startDate="startDate"
          :endDate="endDate"
          :maxRangeLimit="maxRangeDays"
          :limitUnit="'day'"
          :selectingRange="selectingRange"
          :available-dates="availableDates"
          @select-year="handleYearSelection"
          key="years-grid"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import clsx from 'clsx';
import { calendar } from '../../lib';
import CalendarGrid from '../calendar-grid';
import CalendarHeader from '../calendar-header';
import MonthsGrid from '../months-grid';
import YearsGrid from '../years-grid';

export default {
  props: {
    /**
     * If true, the entire calendar grid is disabled.
     * @type {boolean}
     */
    disable: {
      type: Boolean,
      default: false,
    },
    /**
     * Defines the size of the entire calendar component.
     * @type {string}
     * @values 'normal', 'small'
     */
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['normal', 'small'].includes(value)
    },
    /**
     * The maximum number of days allowed in a selected range (inclusive).
     * If null, there is no limit.
     * @type {number | null}
     */
    maxRangeDays: {
      type: Number,
      default: null,
      validator: (value) => value === null || value > 0,
    },
    /**
     * If true, all calendar grids will have the same height.
     */
    gridFixedHeight: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [ calendar ],
  components: { CalendarHeader, CalendarGrid, MonthsGrid, YearsGrid },
  methods: {
    clsx,
    /**
     * Navigates the calendar to the previous or next month.
     * @param {number} direction - -1 for previous month, 1 for next month.
     */
    navigateMonth(direction) {
      this.currentDate = this.addMoment(this.currentDate, direction, 'month');
      this.hideAllSelectors();
    },
    /**
     * Toggles the visibility of the month or year selector grid.
     * Hides the other selector if one is opened.
     * @param {string} selectorType - 'months' or 'years'.
     */
    toggleSelector(selectorType) {
      if (this.disableCalendar) {
        return;
      }

      if (selectorType === 'months') {
        this.showingYears = false;
        this.showingMonths = !this.showingMonths;
      } else if (selectorType === 'years') {
        this.showingMonths = false;
        this.showingYears = !this.showingYears;
      }
    },
    /**
     * Handles the selection of a month from the MonthsGrid.
     * Updates the current month and hides the month selector.
     * @param {number} monthIndex - The 0-indexed month (0 for January).
     */
    handleMonthSelection(monthIndex) {
      this.currentDate = this.setMonthMoment(this.currentDate, monthIndex);
      this.showingMonths = false;
    },
    /**
     * Handles the selection of a year from the YearsGrid.
     * Updates the current year and hides the year selector.
     * @param {number} year - The selected year.
     */
    handleYearSelection(year) {
      this.currentDate = this.setYearMoment(this.currentDate, year);
      this.showingYears = false;
    },
    /**
     * Calculates the number of days between two Moment.js objects (inclusive).
     * @param {object} startMoment - The start Moment.js object.
     * @param {object} endMoment - The end Moment.js object.
     * @returns {number} The number of days in the range (e.g., Jan 1 - Jan 1 is 1 day).
     */
    getDaysDifference(startMoment, endMoment) {
      const d1 = this.toDateMoment(startMoment);
      const d2 = this.toDateMoment(endMoment);
      const diffTime = Math.abs(d2.getTime() - d1.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays + 1;
    },
    /**
     * Handles the selection of a date from the CalendarGrid.
     * Manages single date selection or range selection logic.
     * Emits 'input', 'date-selected', 'range-start-selected', or 'range-selected' events.
     * @param {object} dateObj - The date object selected from CalendarGrid.
     */
    handleDateSelection(dateObj) {
      if (dateObj.isDisabled) {
        return;
      }

      if (this.range) {
        if (!this.selectingRange || this.endDate) {
          this.startDate = dateObj.date;
          this.endDate = null;
          this.selectingRange = true;
          this.$emit('range-start-selected', this.toDateMoment(this.startDate));
        } else {
          let tempStartDate = this.startDate;
          let tempEndDate = dateObj.date;

          if (this.isAfterMoment(tempStartDate, tempEndDate, 'day')) {
            [tempStartDate, tempEndDate] = [tempEndDate, tempStartDate];
          }

          if (this.maxRangeDays !== null) {
            const daysInSelection = this.getDaysDifference(tempStartDate, tempEndDate);
            if (daysInSelection > this.maxRangeDays) {
              this.$emit('range-limit-exceeded', {
                selectedDays: daysInSelection,
                maxAllowed: this.maxRangeDays,
              });
              return;
            }
          }

          this.startDate = tempStartDate;
          this.endDate = tempEndDate;
          this.selectingRange = false;
          this.$emit('range-selected', {
            startDate: this.toDateMoment(this.startDate),
            endDate: this.toDateMoment(this.endDate),
          });
          this.hideAllSelectors();
        }
        this.$emit('input', [
          this.startDate ? this.toDateMoment(this.startDate) : null,
          this.endDate ? this.toDateMoment(this.endDate) : null,
        ]);
      } else {
        const incoming = this.toDateMoment(dateObj.date);
        const current = this.selectedDate ? this.toDateMoment(this.selectedDate) : null;

        if (current && incoming.getTime() === current.getTime()) {
          this.hideAllSelectors();
          return;
        }

        this.selectedDate = dateObj.date;
        this.$emit('input', incoming);
        this.$emit('date-selected', incoming);
        this.hideAllSelectors();
      }
    },
    /**
     * Hides both the month and year selector grids.
     */
    hideAllSelectors() {
      this.showingYears = false;
      this.showingMonths = false;
    },
  },
};
</script>

<style lang="scss" scoped>
$small-scale-factor: 0.8;
$base-font-size: 20px;

.calendar {
  background: white;
  font-family: inherit;
  font-size: $base-font-size;
  font-weight: 400;
  color: #09090b;
  overflow: hidden;

  &-wrapper {
    position: relative;
    flex-grow: 1;
    max-height: 300px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  &--small {
    font-size: $base-font-size * $small-scale-factor;
    width: 273px;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.15s ease;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
}

.calendar-grid {
  &__alignment {
    padding: 4px 0;
  }
}

.months-grid {
  &__alignment {
    padding: 56px 0;
  }
}

.years-grid {
  &__alignment {
    padding: 0;
  }
}
</style>
